import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { useMutation } from '../lib/use-api';
import { ErrorNotice } from './Feedback';

export interface GroupFormValue {
  principalId: string;
  displayName: string;
  description: string | null;
  autoApprove: boolean;
  joinEnabled: boolean;
}

/**
 * Tạo và sửa nhóm.
 *
 * `autoApprove` trước đây bị đặt cứng `true` lúc tạo, nên nhánh duyệt thành
 * viên gần như không bao giờ chạy — nút "Duyệt" có mà không cách nào làm nó
 * xuất hiện. Giờ là lựa chọn tường minh của người tạo.
 *
 * `joinEnabled` tách riêng khỏi `autoApprove`: khoá mã tham gia (hết đợt ghi
 * danh) khác với duyệt từng người (vẫn nhận nhưng cần xác nhận).
 */
export function GroupDialog({
  group,
  onClose,
  onSaved,
}: {
  /** Bỏ trống = tạo mới. */
  group?: GroupFormValue;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const editing = group !== undefined;

  const [displayName, setDisplayName] = useState(group?.displayName ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [autoApprove, setAutoApprove] = useState(group?.autoApprove ?? true);
  const [joinEnabled, setJoinEnabled] = useState(group?.joinEnabled ?? true);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  /**
   * Trả `true` khi lưu xong thay vì để `apiFetch` trả thẳng.
   *
   * PUT trả 204 → `apiFetch` cho `undefined`, mà `useMutation.run` cũng trả
   * `undefined` khi có lỗi — hai trường hợp không phân biệt được. Và KHÔNG
   * dùng được `save.error` ngay sau `await`: nó là React state, lượt render
   * hiện tại vẫn giữ giá trị cũ, nên dialog sẽ đóng như thể thành công trong
   * khi lưu vừa thất bại.
   */
  const save = useMutation<void, boolean>(
    useCallback(async () => {
      const body = {
        displayName: displayName.trim(),
        description: description.trim() === '' ? null : description.trim(),
        autoApprove,
        joinEnabled,
      };
      if (editing) {
        await apiFetch<void>(`/api/groups/${group.principalId}`, { method: 'PUT', body });
      } else {
        await apiFetch<unknown>('/api/groups', { method: 'POST', body });
      }
      return true;
    }, [editing, group?.principalId, displayName, description, autoApprove, joinEnabled]),
  );

  const submit = async (): Promise<void> => {
    if (!displayName.trim()) return;
    if (await save.run()) onSaved();
  };

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <h2>{editing ? t('groups.editHeading') : t('groups.createHeading')}</h2>

      <label className="field">
        <span>{t('groups.newName')}</span>
        <input
          type="text"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
      </label>

      <label className="field">
        <span>{t('groups.description')}</span>
        <input
          type="text"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>

      <label className="field field--inline">
        <input
          type="checkbox"
          checked={autoApprove}
          onChange={(event) => setAutoApprove(event.target.checked)}
        />
        <span>{t('groups.autoApprove')}</span>
      </label>
      <small className="hint">{t('groups.autoApproveHint')}</small>

      <label className="field field--inline">
        <input
          type="checkbox"
          checked={joinEnabled}
          onChange={(event) => setJoinEnabled(event.target.checked)}
        />
        <span>{t('groups.joinEnabled')}</span>
      </label>
      <small className="hint">{t('groups.joinEnabledHint')}</small>

      {save.error ? <ErrorNotice error={save.error} /> : null}

      <footer className="dialog__footer">
        <button type="button" onClick={() => dialogRef.current?.close()}>
          {t('common:action.cancel')}
        </button>
        <button
          type="button"
          disabled={save.pending || !displayName.trim()}
          onClick={() => void submit()}
        >
          {t('common:action.save')}
        </button>
      </footer>
    </dialog>
  );
}
